/* eslint-disable react-hooks/exhaustive-deps */
import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../../../utils/themeCreator";
import { Button, CircularProgress } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { CameraInfoRowAutoPatrol } from "./CameraInfoRowAutoPatrol";
import Tooltip from "@mui/material/Tooltip";
import { getAndSaveSiteImages } from "./SiteAboutPageUtils/getAndSaveSiteImages";
import {
  SiteCameras,
  SiteCamerasResult,
} from "./SiteAboutPageUtils/siteAboutInterfaces";
import { NavigateFunction, Link } from "react-router-dom";
import { EnhancedCameraTableHeadAutoPatrol } from "./EnhancedCameraTableHeadAutoPatrol";
import { Order } from "../../Alerts/AlertsUtils/alertsInterfaces";
import {
  stableSort,
  getComparator,
} from "../../Alerts/AlertsUtils/alertsTableFunctions";
import SyncIcon from "@mui/icons-material/Sync";
import { handleRefreshCameras } from "./SiteAboutPageUtils/handleRefreshCameras";
import { useSiteAboutStore } from "./siteAboutStore/siteAboutStore";
import { ACTIVE_PRODUCTS_PARENT_CATEGORIES } from "./SiteAboutPageUtils/activeProductsParentCategories";
import Box from "@mui/material/Box";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { BulkUpdateProductsDialog } from "./BulkUpdateProductsDialog";
import { AUTOPATROL_PRODUCT_BULK_ROWS } from "./BulkUpdateWizardUtils";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";

type Dispatcher<S> = React.Dispatch<React.SetStateAction<S>>;

export const CamerasTableAutoPatrol = ({
  navigate,
  siteCameras,
  page,
  rowsPerPage,
  selected,
  isSelected,
  handleSingleCheckboxClick,
  setSelected,
  setPage,
  setRowsPerPage,
  setBackdropLoadingScreenOn,
  setNoAutoAddCamerasDialogOpen,
  siteInfo,
  setSiteInfo,
  setSiteSchedules,
  setSiteCameras,
  setActiveTab,
  setDataLoaded,
  setAllSiteSchedules,
  setVmsVariables,
  setArmedStatus,
  setAiLinkSite,
  setSiteDeployed,
  setCamerasNotDeployed,
  setToolTipTitle,
  setConfigureMotion,
  setPatchObject,
  setArmButtonDisabled,
  setRecordingServerData,
  setRecordingServerPatchObject,
  setRecordingServerVisible,
  setHealthCheckData,
  setCamerasRefreshing,
  permissions,
  setWebhooksInfo,
  setWebhooksPatchObject,
  siteSchedules,
  setAlertState,
}: {
  navigate: NavigateFunction;
  siteCameras: SiteCameras;
  page: number;
  rowsPerPage: number;
  selected: readonly string[];
  isSelected: (id: string | number) => boolean;
  handleSingleCheckboxClick: (
    event: React.MouseEvent<unknown>,
    id: string | number
  ) => void;
  setSelected: Dispatcher<readonly string[]>;
  setPage: Dispatcher<number>;
  setRowsPerPage: Dispatcher<number>;
  setBackdropLoadingScreenOn: Dispatcher<boolean>;
  setNoAutoAddCamerasDialogOpen: Dispatcher<boolean>;
  siteInfo: any;
  setSiteInfo: any;
  setSiteSchedules: any;
  setActiveTab: any;
  setDataLoaded: any;
  setAllSiteSchedules: any;
  setVmsVariables: any;
  setArmedStatus: any;
  setAiLinkSite: any;
  setSiteDeployed: any;
  setCamerasNotDeployed: any;
  setToolTipTitle: any;
  setConfigureMotion: any;
  setPatchObject: any;
  setArmButtonDisabled: any;
  setRecordingServerData: any;
  setRecordingServerPatchObject: any;
  setRecordingServerVisible: Dispatcher<boolean>;
  setHealthCheckData: any;
  setCamerasRefreshing: Dispatcher<boolean>;
  permissions: any;
  setWebhooksInfo: any;
  setWebhooksPatchObject: any;
  siteSchedules: any;
  setSiteCameras: Dispatcher<SiteCameras>;
  setAlertState: any;
}) => {
  const [expandedObject, setExpandedObject] = React.useState<{
    [key: string]: boolean;
  }>({});
  const [srcImages, setSrcImages] = React.useState<Record<string, unknown>>({});
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState("camera_name");
  const [renderedRows, setRenderedRows] = React.useState(siteCameras.results);
  const [allExpanded, setAllExpanded] = React.useState(false);
  const [expandedScheduleContext, setExpandedScheduleContext] = React.useState<
    Record<string, { scheduleId: number | null; patrolType: string | null }>
  >({});
  const [count, setCount] = React.useState(0);
  const [bulkUpdateDialogOpen, setBulkUpdateDialogOpen] = React.useState(false);
  const { isFeatureEnabled } = useFeatureFlag();
  const showBulkUpdating = isFeatureEnabled("showBulkUpdating", false);

  const {
    handleFilterCameras,
    selectedAutoPatrolScheduleIndex,
    autoPatrolSchedules,
    activeCameraProducts,
    activeCameraProductsLoading,
    prefetchBulkDialogActiveCameraProducts,
  } = useSiteAboutStore();

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const saveExpandedObject = (objectId: string | number) => {
    const objectKey = String(objectId);
    setExpandedObject((previousState: { [key: string]: boolean }) => {
      const isExpanding = !previousState[objectKey];
      if (isExpanding) {
        const selectedSchedule = autoPatrolSchedules[selectedAutoPatrolScheduleIndex];
        setExpandedScheduleContext((previousContext) => ({
          ...previousContext,
          [objectKey]: {
            scheduleId: selectedSchedule?.id ?? null,
            patrolType: selectedSchedule?.patrolType ?? null,
          },
        }));
      } else {
        setExpandedScheduleContext((previousContext) => {
          const nextContext = { ...previousContext };
          delete nextContext[objectKey];
          return nextContext;
        });
      }
      return {
        ...previousState,
        [objectKey]: isExpanding,
      };
    });
  };

  const saveExpandedObjectAllTrue = () => {
    const selectedSchedule = autoPatrolSchedules[selectedAutoPatrolScheduleIndex];
    setExpandedScheduleContext((previousContext) => {
      const nextContext = { ...previousContext };
      siteCameras.results.forEach((camera) => {
        nextContext[String(camera.id)] = {
          scheduleId: selectedSchedule?.id ?? null,
          patrolType: selectedSchedule?.patrolType ?? null,
        };
      });
      return nextContext;
    });
    setExpandedObject((previousState: { [key: string]: boolean }) => {
      const nextState = { ...previousState };
      Object.keys(nextState).forEach((key) => {
        nextState[key] = true;
      });
      return nextState;
    });
  };

  const saveExpandedObjectAllFalse = () => {
    setExpandedScheduleContext({});
    setExpandedObject((previousState: { [key: string]: boolean }) => {
      const nextState = { ...previousState };
      Object.keys(nextState).forEach((key) => {
        nextState[key] = false;
      });
      return nextState;
    });
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  React.useEffect(() => {
    siteCameras.results.forEach((object: SiteCamerasResult) => {
      setExpandedObject((prev) => ({ ...prev, [String(object.id)]: false }));
    });
  }, []);

  React.useEffect(() => {
    const filteredCameras = handleFilterCameras(siteCameras.results);
    setCount(filteredCameras.length);
    setRenderedRows(filteredCameras);
  }, [siteCameras, selectedAutoPatrolScheduleIndex]);

  const cameraIdsSignature = React.useMemo(
    () => siteCameras?.results?.map((c: SiteCamerasResult) => c.id).join(",") ?? "",
    [siteCameras?.results]
  );

  const siteCamerasRef = React.useRef(siteCameras);
  siteCamerasRef.current = siteCameras;

  React.useEffect(() => {
    if (!cameraIdsSignature) {
      return;
    }
    getAndSaveSiteImages(siteCamerasRef.current, setSrcImages, navigate);
  }, [cameraIdsSignature, navigate]);

  // activeCameraProducts for bulk dialog: prefetch uses /metric/simplified/ when possible, else general_info per camera.

  const visibleRows = React.useMemo(
    () =>
      stableSort(renderedRows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ) as unknown as SiteCamerasResult[],
    [order, orderBy, page, rowsPerPage, renderedRows]
  );

  const selectedCameras = React.useMemo(
    () =>
      siteCameras.results
        .filter((camera) => selected.includes(String(camera.id)))
        .map((camera) => ({
          id: camera.id,
          camera_name: camera.camera_name,
        })),
    [siteCameras.results, selected]
  );

  const renderedRowIds = React.useMemo(
    () => renderedRows.map((camera) => String(camera.id)),
    [renderedRows]
  );

  const selectedInViewCount = React.useMemo(
    () => renderedRowIds.filter((id) => selected.includes(id)).length,
    [renderedRowIds, selected]
  );

  const handleSelectAllVisible = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected((prevSelected) => {
        const existing = new Set(prevSelected.map(String));
        renderedRowIds.forEach((id) => existing.add(id));
        return Array.from(existing);
      });
      return;
    }
    setSelected((prevSelected) =>
      prevSelected.filter((id) => !renderedRowIds.includes(String(id)))
    );
  };

  const openBulkUpdateDialog = () => {
    const selectedCameraIds = selected.map(Number);
    if (selectedCameraIds.length === 0) return;
    setBulkUpdateDialogOpen(true);
    void prefetchBulkDialogActiveCameraProducts(
      navigate,
      siteInfo?.id,
      selectedCameraIds
    );
  };

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <div className="relative">
          <Typography
            sx={{
              fontFamily: "Mulish",
              fontWeight: "Bold",
              paddingLeft: 2,
              paddingTop: 1,
              paddingBottom: 0.5,
            }}
            variant="h6"
            component="div"
          >
            <Box
              component="span"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              Site Cameras
              <Tooltip
                title="All configurations done at the camera level are global and apply to all schedules. These settings do not change per schedule."
                placement="top"
                sx={{ fontSize: "15px", fontFamily: "Mulish" }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </Tooltip>
            </Box>
            {siteInfo.display_refresh && (
              <div className="absolute left-[160px] top-[6px]">
                <ThemeProvider theme={theme}>
                  <Button
                    type="button"
                    color="primary"
                    size="small"
                    onClick={() => {
                      setBackdropLoadingScreenOn(true);
                      setExpandedObject({});
                      setExpandedScheduleContext({});
                      setCamerasRefreshing(true);
                      handleRefreshCameras(
                        navigate,
                        setSiteInfo,
                        setSiteSchedules,
                        setSiteCameras,
                        setActiveTab,
                        setDataLoaded,
                        setBackdropLoadingScreenOn,
                        setAllSiteSchedules,
                        setVmsVariables,
                        setNoAutoAddCamerasDialogOpen,
                        setArmedStatus,
                        setAiLinkSite,
                        setSiteDeployed,
                        () => {},
                        setAlertState,
                        setCamerasNotDeployed,
                        setToolTipTitle,
                        setConfigureMotion,
                        setPatchObject,
                        setArmButtonDisabled,
                        setRecordingServerData,
                        setRecordingServerPatchObject,
                        setRecordingServerVisible,
                        setHealthCheckData,
                        setCamerasRefreshing,
                        setWebhooksInfo,
                        setWebhooksPatchObject
                      );
                    }}
                  >
                    <SyncIcon fontSize="small" /> &nbsp; Refresh Previews
                  </Button>
                </ThemeProvider>
              </div>
            )}
            {showBulkUpdating && permissions.edit_cameras && (
              <div className="absolute right-[30px] top-[6px]">
                <ThemeProvider theme={theme}>
                  <Button
                    type="button"
                    color="primary"
                    size="small"
                    onClick={openBulkUpdateDialog}
                    disabled={selected.length === 0}
                    sx={{ fontFamily: "Mulish" }}
                  >
                    Bulk Update
                  </Button>
                </ThemeProvider>
              </div>
            )}
          </Typography>
        </div>

        <Table
          sx={{ width: "100%", fontFamily: "Mulish" }}
          aria-label="cameras table"
          size="small"
        >
          <EnhancedCameraTableHeadAutoPatrol
            onSelectAllClick={handleSelectAllVisible}
            numSelected={selectedInViewCount}
            rowCount={renderedRows.length}
            showSelection={showBulkUpdating && permissions.edit_cameras}
            onRequestSort={handleRequestSort}
            orderBy={orderBy}
            order={order}
            allExpanded={allExpanded}
            setAllExpanded={setAllExpanded}
            setExpandedObject={setExpandedObject}
            saveExpandedObjectAllTrue={saveExpandedObjectAllTrue}
            saveExpandedObjectAllFalse={saveExpandedObjectAllFalse}
          />
          <TableBody>
            {visibleRows.map((obj: SiteCamerasResult) => (
              <React.Fragment key={obj.id}>
                <TableRow selected={isSelected(obj.id)}>
                  {showBulkUpdating && permissions.edit_cameras && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isSelected(obj.id)}
                        onClick={(event) => handleSingleCheckboxClick(event, obj.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      fontFamily: "Mulish",
                      fontSize: "14px",
                      paddingLeft: 2,
                      paddingRight: 1,
                      width: "40%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    align="left"
                  >
                    <Tooltip
                      title={
                        srcImages[String(obj.id) as keyof typeof srcImages] ? (
                          <React.Fragment>
                            {
                              srcImages[
                                String(obj.id) as keyof typeof srcImages
                              ]
                            }
                          </React.Fragment>
                        ) : (
                          ""
                        )
                      }
                      placement="right-start"
                    >
                      <Link
                        to={`/sites/${siteInfo.id}/editcamera/${obj.id}`}
                        style={{
                          color: "#0d47a1",
                          textDecoration: "none",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#002171";
                          e.currentTarget.style.textDecoration = "underline";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#0d47a1";
                          e.currentTarget.style.textDecoration = "none";
                        }}
                      >
                        {obj.camera_name ?? "—"}
                      </Link>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Mulish",
                      fontSize: "14px",
                      paddingLeft: 1,
                      paddingRight: 1,
                      width: "40%",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                    align="left"
                  >
                    {activeCameraProductsLoading[obj.id] ? (
                      <CircularProgress size={18} color="primary" />
                    ) : activeCameraProducts[obj.id] === undefined ? (
                      <span
                        style={{ color: "var(--mui-palette-text-secondary)" }}
                      >
                        —
                      </span>
                    ) : (
                      (() => {
                        const products = activeCameraProducts[obj.id];
                        const hasAny = ACTIVE_PRODUCTS_PARENT_CATEGORIES.some(
                          (parent) =>
                            parent.children.some((name) => products?.[name])
                        );
                        if (!hasAny) {
                          return (
                            <span
                              style={{
                                color: "var(--mui-palette-text-secondary)",
                              }}
                            >
                              —
                            </span>
                          );
                        }
                        return (
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.75,
                              flexWrap: "wrap",
                            }}
                          >
                            {ACTIVE_PRODUCTS_PARENT_CATEGORIES.map((parent) => {
                              const activeChildren = parent.children.filter(
                                (name) => products?.[name]
                              );
                              if (activeChildren.length === 0) return null;
                              const { Icon } = parent;
                              return (
                                <Tooltip
                                  key={parent.parentLabel}
                                  title={activeChildren.join(", ")}
                                  placement="top"
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      display: "inline-flex",
                                      lineHeight: 0,
                                    }}
                                  >
                                    <Icon
                                      sx={{
                                        color: parent.color,
                                        fontSize: 28,
                                      }}
                                    />
                                  </Box>
                                </Tooltip>
                              );
                            })}
                          </Box>
                        );
                      })()
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontFamily: "Mulish",
                      fontSize: "14px",
                      paddingLeft: 1,
                      paddingRight: 2,
                      width: "20%",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <IconButton
                      aria-label={
                        expandedObject[String(obj.id)]
                          ? "collapse row"
                          : "expand row"
                      }
                      size="small"
                      onClick={() => {
                        saveExpandedObject(String(obj.id));
                      }}
                    >
                      {expandedObject[String(obj.id)] ? (
                        <KeyboardArrowUpIcon fontSize="large" color="primary" />
                      ) : (
                        <KeyboardArrowDownIcon
                          fontSize="large"
                          color="primary"
                        />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
                <CameraInfoRowAutoPatrol
                  obj={obj}
                  expanded={!!expandedObject[String(obj.id)]}
                  lockedScheduleId={
                    expandedScheduleContext[String(obj.id)]?.scheduleId ?? null
                  }
                  lockedSchedulePatrolType={
                    expandedScheduleContext[String(obj.id)]?.patrolType ?? null
                  }
                  showSelectionColumn={
                    showBulkUpdating && permissions.edit_cameras
                  }
                  setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
                  siteInfo={siteInfo}
                  permissions={permissions}
                  srcImages={srcImages}
                />
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {showBulkUpdating && (
        <BulkUpdateProductsDialog
          open={bulkUpdateDialogOpen}
          selectedCameras={selectedCameras}
          activeCameraProducts={activeCameraProducts}
          activeCameraProductsLoading={activeCameraProductsLoading}
          siteInfo={siteInfo}
          navigate={navigate}
          productRows={AUTOPATROL_PRODUCT_BULK_ROWS}
          onClose={() => {
            setBulkUpdateDialogOpen(false);
            setSelected([]);
          }}
          onApply={() => {
            setBulkUpdateDialogOpen(false);
            setSelected([]);
          }}
        />
      )}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};
